import { useEffect } from "react";
import { Box, IconButton, Slider, Typography } from "@mui/material";
import useTranslationSetup from "../../utils/useTranslationSetup";
import useAudioPlayer from "../../utils/useAudioPlayer";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseIcon from "@mui/icons-material/Pause";
import "./styles.css";
import { getUserProperties } from "../../utils/helper";
import { produceEventLog } from "../../utils/helper";
import useParamValues from "../../utils/useParamValues";

function AudioPlayer({
  audioBlob,
  audioBlobUrl,
  audioBlobRef,
  setAudioBlobRef,
  currentStep,
  onPlayEventName,
  onPauseEventName,
}) {
  const {
    isPlaying,
    currentTime,
    duration,
    audioElementRef,
    handlePlayPause,
    handleTimeUpdate,
    handleSliderChange,
    formatTime,
    setDuration,
  } = useAudioPlayer();
  const { activeLanguage } = useTranslationSetup();
  const userProperties = getUserProperties(useParamValues());

  useEffect(() => {
    if (audioElementRef !== null && audioBlobRef !== false) {
      setAudioBlobRef(audioElementRef);
    }
  }, [audioElementRef, audioBlobRef]);

  useEffect(() => {
    if (audioElementRef.current && audioBlobUrl) {
      audioElementRef.current.load();
    }
  }, [audioBlobUrl]);

  const onPlayPauseClick = () => {
    handlePlayPause();
    if (!isPlaying) {
      produceEventLog(onPlayEventName, {
        ...userProperties,
        assessment_flow: currentStep,
      });
    } else {
      produceEventLog(onPauseEventName, {
        ...userProperties,
        assessment_flow: currentStep,
      });
    }
  };

  return (
    <Box className="audio-player-container">
      {currentTime === duration ? (
        <IconButton onClick={onPlayPauseClick}>
          {audioBlob ? (
            <PlayArrowIcon fontSize="medium" />
          ) : (
            <PlayArrowRoundedIcon
              fontSize="medium"
              className="audio-player-rounded-btn"
            />
          )}
        </IconButton>
      ) : (
        <Box className="audio-player-icon">
          <IconButton onClick={onPlayPauseClick}>
            {isPlaying ? (
              <PauseIcon fontSize="medium" />
            ) : (
              <PlayArrowIcon fontSize="medium" />
            )}
          </IconButton>
        </Box>
      )}
      <audio
        ref={audioElementRef}
        onDurationChange={(e) => {
          const nextDuration = e.currentTarget.duration;
          if (
            nextDuration &&
            !Number.isNaN(nextDuration) &&
            nextDuration !== Infinity
          ) {
            setDuration(nextDuration);
            e.currentTarget.currentTime = 0;
          }
        }}
        onTimeUpdate={(e) => {
          handleTimeUpdate(e);
        }}
        onLoadedMetadata={(e) => {
          e.currentTarget.currentTime = 1e101;
        }}
        preload="metadata"
      >
        <source
          src={
            audioBlobUrl || (audioBlob ? URL.createObjectURL(audioBlob) : null)
          }
          type="audio/wav"
        />
      </audio>
      <Box className="audio-slider">
        <Slider
          value={currentTime}
          max={duration}
          onChange={handleSliderChange}
        />
      </Box>
      <Typography
        fontSize="14px"
        color="black.hot"
        className="custom-ff-regular"
        lang={activeLanguage}
      >
        {formatTime(Math.max(0, duration - currentTime))}
      </Typography>
    </Box>
  );
}

export default AudioPlayer;
